import React from "react";
import { ActionIcon, Group, SegmentedControl, Select, Tooltip } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconRefresh } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { FacetValue } from "@models/adminMonitoring";
import { WINDOW_PRESETS, type WindowPreset } from "@hooks/monitoring/useMonitoringFilters";
import { formatCompact, moduleLabel } from "@utils/monitoringFormat";

interface WindowToolbarProps {
  preset: WindowPreset;
  custom: { from: string | null; to: string | null };
  onPresetChange: (preset: WindowPreset, range?: { from: string; to: string }) => void;
  onRefresh: () => void;
  /** Shown only to cross-organisation callers; empty value = every organisation. */
  organisations?: FacetValue[];
  organisationId?: string;
  onOrganisationChange?: (value: string | null) => void;
  /** When set, a module filter right of the organisation selector; empty value = every module. */
  modules?: FacetValue[];
  moduleValue?: string;
  onModuleChange?: (value: string | null) => void;
  loading?: boolean;
}

const WindowToolbar: React.FC<WindowToolbarProps> = ({
  preset, custom, onPresetChange, onRefresh, organisations, organisationId, onOrganisationChange,
  modules, moduleValue, onModuleChange, loading,
}) => {
  const { t } = useTranslation();
  const range: [Date | null, Date | null] = [
    custom.from ? dayjs(custom.from).toDate() : null,
    custom.to ? dayjs(custom.to).toDate() : null,
  ];
  const orgOptions = organisations
    ? [
        ...organisations.map((o) => ({ value: o.value, label: o.label ?? o.value })),
        ...(organisationId && !organisations.some((o) => o.value === organisationId)
          ? [{ value: organisationId, label: organisationId }]
          : []),
      ]
    : null;

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <SegmentedControl
        size="sm"
        value={preset}
        onChange={(value) => onPresetChange(value as WindowPreset)}
        data={WINDOW_PRESETS.map((value) => ({ value, label: t(`admin.insights.window.${value}`) }))}
        aria-label={t("admin.insights.window.label")}
      />
      {preset === "custom" && (
        <DatePickerInput
          type="range"
          size="sm"
          w={260}
          value={range}
          maxDate={new Date()}
          aria-label={t("admin.insights.window.custom")}
          placeholder={t("admin.insights.window.pick")}
          onChange={([from, to]) => {
            if (from && to) {
              onPresetChange("custom", {
                from: dayjs(from).format("YYYY-MM-DD"),
                to: dayjs(to).format("YYYY-MM-DD"),
              });
            }
          }}
        />
      )}
      {orgOptions && onOrganisationChange && (
        <Select
          size="sm"
          w={240}
          searchable
          clearable
          data={orgOptions}
          value={organisationId ?? null}
          onChange={onOrganisationChange}
          placeholder={t("admin.insights.filters.allOrganisations")}
          aria-label={t("admin.insights.filters.organisation")}
        />
      )}
      {modules && onModuleChange && (
        <Select
          size="sm"
          w={220}
          searchable
          clearable
          data={[
            ...modules.map((m) => ({ value: m.value, label: `${moduleLabel(m.value)} (${formatCompact(m.event_count)})` })),
            ...(moduleValue && !modules.some((m) => m.value === moduleValue)
              ? [{ value: moduleValue, label: moduleLabel(moduleValue) }]
              : []),
          ]}
          value={moduleValue ?? null}
          onChange={onModuleChange}
          placeholder={t("admin.insights.filters.allModules")}
          aria-label={t("admin.insights.filters.module")}
          nothingFoundMessage={t("admin.insights.filters.noValues")}
        />
      )}
      <Tooltip label={t("admin.insights.refresh")}>
        <ActionIcon variant="default" size="lg" onClick={onRefresh} loading={loading}
                    aria-label={t("admin.insights.refresh")}>
          <IconRefresh size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};

export default WindowToolbar;
